import React, { useCallback, useContext, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { COMPANY_PLACEHOLDER_TEXT } from "@helpers/types";
import {
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  Container,
} from "@mui/material";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import useApi from "@helpers/hooks/useApi";
import { AuthContext } from "@storage/AuthProvider";
import { generateCompanyCode } from "@helpers/utils";
import { FormFactorEnum } from "@api";
import { useRouter } from "next/router";
import PrettyButton from "@components/common/PrettyButton";
import { styled } from "@mui/material/styles";
import { gtagReportConversion } from "@helpers/AnalyticsHelper";
import ThirdPartyScripts from "@components/common/ThirdPartyScripts";
import {
  FormContainerStyled,
  FormTitle,
  BenefitsContainer,
  BenefitItem,
  BenefitIcon,
  BenefitText,
  FormWrapper,
  FormRow,
  FormField,
  FieldLabel,
  FormSection,
  TermsSection,
  SubmitContainer,
} from "./free-report.styles";
import { toast } from "react-toastify";

type FormData = {
  email: string;
  projectUrl: string;
  description: string;
  termsAccepted: boolean;
};

type ContentConfig = {
  benefits: string[];
  subtitle: string;
  ctaTitle: string;
};

type FreeReportFormProps = {
  isFree?: boolean;
  content?: ContentConfig;
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "5px 5px theme.palette.color.content.shadowBlueLight",
    fontSize: 12,
    border: "1px solid lightgrey",
    borderRadius: "0.5rem",
  },
}));

const FreeReportForm: React.FC<FreeReportFormProps> = ({
  isFree = true,
  content = {
    benefits: [
      "TestTheTest tests your application & writes your test book",
      "Get your report within three working days",
      "No source code access, no technical setup needed",
    ],
    subtitle: isFree
      ? "Try it for free. If you like it, plans start at $390/month."
      : "Plans start at $390/month. Cancel anytime.",
    ctaTitle: isFree ? "Give me my free report!" : "Start testing!",
  },
}) => {
  const api = useApi();
  const { updateAccessToken, setAccessToken } = useContext(AuthContext);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      email: "",
      projectUrl: "",
      description: "",
      termsAccepted: false,
    },
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      setIsLoading(true);
      // First things first, create a new account
      const tempPassword = Math.random().toString(36).slice(-8);
      try {
        await api.apiV1RegistrationCreate({
          first_name: "Admin",
          last_name: `@${data.projectUrl}`,
          email: data.email,
          password1: tempPassword,
          password2: tempPassword,
          company: {
            name: data.projectUrl,
            address_1: COMPANY_PLACEHOLDER_TEXT,
            city: COMPANY_PLACEHOLDER_TEXT,
            post_code: COMPANY_PLACEHOLDER_TEXT,
            country: COMPANY_PLACEHOLDER_TEXT,
          },
        });
      } catch (error: unknown) {
        const message =
          error != undefined && typeof error === "object" && "message" in error
            ? String(error.message)
            : "Unknown error";
        toast.error(`Failed to create account: ${message}`);
        setIsLoading(false);
        return;
      }

      // Once the user is created, we need to log them in
      const { data: loginResponse } = await api.apiV1LoginCreate({
        email: data.email,
        password: tempPassword,
      });
      const token =
        typeof loginResponse === "object" && "key" in loginResponse
          ? String(loginResponse.key)
          : undefined;
      if (!token) {
        throw new Error("Failed to log in");
      }
      await updateAccessToken(token);
      setAccessToken(token);

      let domain = data.projectUrl;
      try {
        domain = new URL(data.projectUrl).hostname;
      } catch (error) {
        console.error(error);
      }

      // Also create their project and platform (needs the access token to be set)
      const { data: { id: projectId } = {} } = await api.apiV1ProjectsCreate({
        name: domain,
        code: generateCompanyCode(domain),
        description: domain,
      });
      if (!projectId) {
        throw new Error("Project was created but no ID was returned");
      }
      await api.apiV1PlatformsCreate(String(projectId), {
        available_environment: ["Default"],
        form_factor: FormFactorEnum.W,
        access_instruction: data.projectUrl,
        description: data.description,
      });
      router.push("/projects");

      gtagReportConversion();
      setIsLoading(false);
    },
    [api, router, updateAccessToken, setAccessToken],
  );

  return (
    <Container maxWidth="md" sx={{ padding: 0 }}>
      <ThirdPartyScripts />

      <FormContainerStyled id="FreeReportForm" elevation={0}>
        <FormTitle variant="h2" gutterBottom>
          Sign up and{" "}
          <span style={{ color: "#5050f9" }}>
            get your {isFree ? "free" : ""} bug report!
          </span>
        </FormTitle>

        {/* <FormSubtitle>{content.subtitle}</FormSubtitle> */}

        <BenefitsContainer>
          {content.benefits.map((benefit, index) => (
            <BenefitItem key={index}>
              <BenefitIcon>✅</BenefitIcon>
              <BenefitText>{benefit}</BenefitText>
            </BenefitItem>
          ))}
        </BenefitsContainer>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormWrapper>
            <FormRow>
              <FormField>
                <FieldLabel>
                  URL to test{" "}
                  <LightTooltip
                    title="Where do we find your app?"
                    placement="top-end"
                  >
                    <i className="fa-solid fa-circle-info" />
                  </LightTooltip>
                </FieldLabel>
                <Controller
                  name="projectUrl"
                  control={control}
                  rules={{ required: "URL is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Website, App Store or Play Store link"
                      error={!!errors.projectUrl}
                      helperText={errors.projectUrl?.message}
                    />
                  )}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField>
                <FieldLabel>Your e-mail address</FieldLabel>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="E-mail address"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </FormField>
            </FormRow>

            <FormSection>
              <FieldLabel>
                Test instructions or login info (optional){" "}
                <LightTooltip
                  title="Please include all details we need to access your app. (Secrets can be shared securely after signup.)"
                  placement="top-end"
                >
                  <i className="fa-solid fa-circle-info" />
                </LightTooltip>
              </FieldLabel>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="e.g. 'Please sign up using a @testthetest.com email'"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </FormSection>

            <TermsSection>
              <Controller
                name="termsAccepted"
                control={control}
                rules={{ required: "You must accept the terms and conditions" }}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={
                      <Typography>
                        I have read and accepted the{" "}
                        <Link href="/terms/general">Terms and Conditions</Link>,
                        and I allow TestTheTest to access my app for testing
                        purposes. Subject to availability.
                        {isFree ? " New customers only." : ""}
                      </Typography>
                    }
                  />
                )}
              />
              {errors.termsAccepted && (
                <Typography color="error" variant="caption">
                  {errors.termsAccepted.message}
                </Typography>
              )}
            </TermsSection>

            <SubmitContainer>
              <PrettyButton
                variant="primary"
                size="md"
                disabled={!isValid}
                isLoading={isLoading}
                onClick={handleSubmit(onSubmit)}
                title={content.ctaTitle}
              />
            </SubmitContainer>
          </FormWrapper>
        </form>
      </FormContainerStyled>
    </Container>
  );
};

export default FreeReportForm;
